import { useMutation, useQuery } from '@tanstack/react-query';
import {
  DraftOutreachResponse,
  ListOutreachMessagesResponse,
  RefineOutreachResponse,
  SendOutreachResponse,
  type DraftOutreachRequest,
  type OutreachTemplateKind,
  type RefineOutreachRequest,
  type SendOutreachRequest,
} from '@sourcito/schemas';
import { apiFetch } from './api';

/** The outreach thread for a supplier, newest first (incl. daemon-classified replies). */
export function useSupplierMessages(supplierId: string) {
  return useQuery({
    queryKey: ['supplier-messages', supplierId] as const,
    queryFn: () =>
      apiFetch<ListOutreachMessagesResponse>(`/api/suppliers/${supplierId}/messages`, {
        responseSchema: ListOutreachMessagesResponse,
      }),
  });
}

export interface UseDraftOutreachVars {
  supplierId: string;
  templateKind: OutreachTemplateKind;
}

/**
 * Draft an outreach email via OpenRouter (BYOK). Single LLM call,
 * server-side audited. Returns the editable subject + body the modal
 * pre-fills. The caller is responsible for clipboard copy and any
 * follow-up status advance (via useUpdateSupplier).
 */
export function useDraftOutreach() {
  return useMutation({
    mutationFn: async (vars: UseDraftOutreachVars) => {
      const body: DraftOutreachRequest = { templateKind: vars.templateKind };
      return apiFetch<DraftOutreachResponse>(`/api/suppliers/${vars.supplierId}/draft-outreach`, {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: DraftOutreachResponse,
      });
    },
  });
}

export interface UseSendOutreachVars {
  supplierId: string;
  subject: string;
  body: string;
  claimId?: string;
}

/**
 * Queue one email for the mail daemon (Phase B.5). The click on Send is the
 * consent gate: the daemon delivers exactly what was queued, from the
 * tenant's own mailbox, and advances the linked claim on actual send.
 */
export function useSendOutreach() {
  return useMutation({
    mutationFn: async (vars: UseSendOutreachVars) => {
      return apiFetch<SendOutreachResponse>(`/api/suppliers/${vars.supplierId}/send-outreach`, {
        method: 'POST',
        body: JSON.stringify({
          subject: vars.subject,
          body: vars.body,
          ...(vars.claimId ? { claimId: vars.claimId } : {}),
        } satisfies SendOutreachRequest),
        responseSchema: SendOutreachResponse,
      });
    },
  });
}

// ─────────────────────────────────────────────────────────
// Refine — one-shot revision of the CURRENT edited subject/body per an
// explicit user instruction (a preset button or free text). Exported as
// consts (not inlined in the component) so the exact copy is unit-testable
// and reusable from both DossierPage and SupplierDetailPage.
// ─────────────────────────────────────────────────────────

export const OUTREACH_REFINE_PRESET_SHORTER =
  'Make this email about half as long. Keep the specific ask and anything factual; cut pleasantries first.';

export const OUTREACH_REFINE_PRESET_PROFESSIONAL =
  'Make the tone more professional and businesslike. No slang, no exclamation marks, keep it concise.';

export const OUTREACH_REFINE_PRESET_WARMER =
  'Make the tone a bit warmer and more personable without getting informal or wordy.';

export const OUTREACH_REFINE_PRESETS: ReadonlyArray<{ label: string; instruction: string }> = [
  { label: 'Shorter', instruction: OUTREACH_REFINE_PRESET_SHORTER },
  { label: 'More professional', instruction: OUTREACH_REFINE_PRESET_PROFESSIONAL },
  { label: 'Warmer', instruction: OUTREACH_REFINE_PRESET_WARMER },
];

export interface UseRefineOutreachVars {
  supplierId: string;
  subject: string;
  body: string;
  instruction: string;
}

/**
 * Refine an already-drafted (possibly hand-edited) email via one LLM call.
 * No query invalidation — nothing about this persists server-side.
 */
export function useRefineOutreach() {
  return useMutation({
    mutationFn: async (vars: UseRefineOutreachVars) => {
      const body: RefineOutreachRequest = {
        subject: vars.subject,
        body: vars.body,
        instruction: vars.instruction,
      };
      return apiFetch<RefineOutreachResponse>(`/api/suppliers/${vars.supplierId}/refine-outreach`, {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: RefineOutreachResponse,
      });
    },
  });
}

// ─────────────────────────────────────────────────────────
// Composer state — pure reducer shared by every editable+refinable
// subject/body surface (DossierPage's DraftEmailDialog,
// SupplierDetailPage's DraftOutreachForm). Kept pure + exported so the
// seed/edit/refine/revert transitions are table-driven-testable without a
// component test.
// ─────────────────────────────────────────────────────────

export interface OutreachComposerState {
  subject: string;
  body: string;
  /** The pre-refine subject/body, restorable via one "Revert" — single level of undo. */
  previous: { subject: string; body: string } | null;
}

export const initialOutreachComposerState: OutreachComposerState = {
  subject: '',
  body: '',
  previous: null,
};

export type OutreachComposerAction =
  /** A fresh draft (or "Draft again") replaces the text and clears any pending revert. */
  | { type: 'seed'; subject: string; body: string }
  /** Direct user typing in the subject/body fields. */
  | { type: 'edit'; field: 'subject' | 'body'; value: string }
  /** A refine call succeeded — apply the revision, remembering the pre-refine text. */
  | { type: 'refined'; subject: string; body: string }
  /** Restore the pre-refine text (only available immediately after a refine). */
  | { type: 'revert' };

export function outreachComposerReducer(
  state: OutreachComposerState,
  action: OutreachComposerAction
): OutreachComposerState {
  switch (action.type) {
    case 'seed':
      return { subject: action.subject, body: action.body, previous: null };
    case 'edit':
      return { ...state, [action.field]: action.value };
    case 'refined':
      return {
        subject: action.subject,
        body: action.body,
        previous: { subject: state.subject, body: state.body },
      };
    case 'revert':
      return state.previous
        ? { subject: state.previous.subject, body: state.previous.body, previous: null }
        : state;
    default:
      return state;
  }
}
